import { Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, InputLabel, OutlinedInput, Stack, Typography } from '@mui/material'
import { FunctionComponent, useEffect } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { DialogSaveButtons } from '../../../presentation-components/miscellaneous-components/dialog-save-buttons'
import NominalCodeMappingService from '../../../../shared/services/nominal-code-mapping/nominal-code-mapping.service'
import { IPostNominalCodeMapping, NominalMappingType } from '../../../../shared/services/nominal-code-mapping/nominal-code-mapping.model'
import { INominalOutcomeValue, NominalMappingOutcomePicker } from '../../../presentation-components/settings-components/data-import/nominal-mapping-outcome-picker'

interface INominalMappingFormValues {
  nominalCode: string
  nominalDescription: string
  mappingType: NominalMappingType
  spendBasedConversionId: string | null
  conversionSourceId: string | null
  excludeReason: string | null
  groupId: string | null
}

export const NominalCodeMappingDialog: FunctionComponent<{
  mappingId?: string,
  open: boolean,
  closeHandler: () => void,
  isEdit: boolean,
  isSubmitting: boolean,
  onSubmit: (values: IPostNominalCodeMapping, resetForm: () => void, isEdit: boolean) => void,
  errorCode?: number,
  errorMessage?: string,
}> = ({
  mappingId,
  open,
  closeHandler,
  isEdit,
  isSubmitting,
  onSubmit,
  errorCode,
  errorMessage,
}) => {
    //Services
    const nominalCodeMappingService = new NominalCodeMappingService()
    //Formik
    const mappingSchema = Yup.object().shape({
      nominalCode: Yup.string().required('You must provide a nominal code'),
      nominalDescription: Yup.string().nullable(),
      mappingType: Yup.string().required(),
      spendBasedConversionId: Yup.string().when('mappingType', {
        is: 'spend',
        then: schema => schema.required('Select a spend activity'),
        otherwise: schema => schema.nullable(),
      }),
    })

    const formik = useFormik<INominalMappingFormValues>({
      initialValues: {
        nominalCode: '',
        nominalDescription: '',
        mappingType: 'spend',
        spendBasedConversionId: null,
        conversionSourceId: null,
        excludeReason: null,
        groupId: null,
      },
      validationSchema: mappingSchema,
      onSubmit: (values) => {
        const payload: IPostNominalCodeMapping = {
          nominalCode: values.nominalCode,
          nominalDescription: values.nominalDescription || null,
          mappingType: values.mappingType,
          spendBasedConversionId: values.mappingType === 'spend' ? values.spendBasedConversionId : null,
          conversionSourceId: values.mappingType === 'spend' ? values.conversionSourceId : null,
          excludeReason: values.mappingType === 'exclude' ? values.excludeReason : null,
          groupId: values.mappingType === 'spend' ? values.groupId : null,
        }
        onSubmit(payload, formik.resetForm, isEdit)
      },
    })

    //Use Effects
    useEffect(() => {
      if (mappingId) {
        nominalCodeMappingService.getOne(mappingId)
          .then(response => {
            formik.setValues({
              nominalCode: response.nominalCode,
              nominalDescription: response.nominalDescription || '',
              mappingType: response.mappingType,
              spendBasedConversionId: response.spendBasedConversionId,
              conversionSourceId: response.conversionSourceId,
              excludeReason: response.excludeReason,
              groupId: response.groupId,
            })
          })
          .catch(err => console.log(err))
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const outcomeChangeHandler = (next: INominalOutcomeValue) => {
      formik.setValues({
        ...formik.values,
        mappingType: next.mappingType,
        spendBasedConversionId: next.spendBasedConversionId ?? null,
        conversionSourceId: next.conversionSourceId ?? null,
        excludeReason: next.excludeReason ?? null,
        groupId: next.groupId ?? null,
      })
    }

    const cancelHandler = () => {
      formik.resetForm()
      closeHandler()
    }

    return (
      <Dialog open={open} onClose={closeHandler} maxWidth='xs' fullWidth>
        <Box sx={{ p: 4 }} component='form' onSubmit={formik.handleSubmit}>
          <DialogTitle sx={{ pt: 0, pl: 0, pr: 0 }}>
            {isEdit ? 'Edit Nominal Code' : 'Add Nominal Code'}
          </DialogTitle>
          <DialogContent sx={{ pt: 0, pl: 0, pr: 0 }}>
            <FormControl fullWidth sx={{ mt: 0.5 }}>
              <InputLabel>Nominal Code</InputLabel>
              <OutlinedInput
                id='nominalCode'
                name='nominalCode'
                label='Nominal Code'
                fullWidth
                disabled={isEdit}
                value={formik.values.nominalCode || ''}
                onChange={formik.handleChange}
                error={formik.touched.nominalCode && Boolean(formik.errors.nominalCode)}
                sx={{ color: 'text.primary' }} />
              {formik.touched.nominalCode && formik.errors.nominalCode &&
                <FormHelperText sx={{ color: '#bf3333', marginLeft: '16px !important' }}>
                  {formik.errors.nominalCode as string}
                </FormHelperText>
              }
              {errorMessage && errorCode === 1 &&
                <FormHelperText sx={{ color: '#bf3333', marginLeft: '16px !important' }}>
                  {errorMessage}
                </FormHelperText>
              }
            </FormControl>

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Description</InputLabel>
              <OutlinedInput
                id='nominalDescription'
                name='nominalDescription'
                label='Description'
                fullWidth
                value={formik.values.nominalDescription || ''}
                onChange={formik.handleChange}
                sx={{ color: 'text.primary' }} />
            </FormControl>

            <Stack direction='row' alignItems='center' sx={{ mt: 3, mb: 1 }}>
              <Typography variant='subtitle1'>Outcome</Typography>
            </Stack>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              Map this nominal code to a spend factor, or exclude it (for example wages or VAT).
            </Typography>

            <NominalMappingOutcomePicker
              value={{
                mappingType: formik.values.mappingType,
                spendBasedConversionId: formik.values.spendBasedConversionId,
                conversionSourceId: formik.values.conversionSourceId,
                excludeReason: formik.values.excludeReason,
                groupId: formik.values.groupId,
              }}
              onChange={outcomeChangeHandler}
            />
            {formik.touched.spendBasedConversionId && formik.errors.spendBasedConversionId &&
              <FormHelperText sx={{ color: '#bf3333', marginLeft: '16px !important', mt: 1 }}>
                {formik.errors.spendBasedConversionId as string}
              </FormHelperText>
            }
          </DialogContent>
          <DialogActions sx={{ p: 0 }}>
            <DialogSaveButtons
              cancelHandler={cancelHandler}
              submitHandler={formik.submitForm}
              isSubmitting={isSubmitting}
            />
          </DialogActions>
        </Box>
      </Dialog>
    )
  }
